import { Middleware } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import { analytics } from '@/lib/analytics'
import { ChatItem, MessageItem, SystemMessageItem } from '@/lib/type'

import { eventNameToUserFacingMessageMap } from '@/context/progressMessageTemplates'
import { fetchAgent } from '@/lib/api'
import { refreshQuota, signOut } from '@/lib/features/user/userThunks'
import { messagesHash } from '@/lib/utils'
import { shuffle } from 'lodash'
import { ApiCodeGenerationEvent, ApiCodeGenerationRequest } from 'sprites-types/api/codegen'
import { setAgent, setAgentDeployed, setAgentSteps, updateAgent } from '../agent/agentSlice'
import {
  addLoadingMessage,
  appendMessage,
  appendSubProgressMessage,
  removeAllLoadingMessages,
  replaceMessages,
  setClarificationInProgress,
  setLastProgressMessageExpanded,
  setLastProgressMessageId,
  updateSubProgressMessage,
} from '../chats/chatSlice'
import {
  getLayoutSetting,
  setErrorMessage,
  setMinimizeCustomizationPanel,
  setShowAppPanel,
  setShowCustomizationPanel,
  setShowErrorWhileGeneration,
  updateLayoutSetting,
} from '../layoutSlice'
import { setPromptLoading, setPrompts, showMemoryPanel } from '../memory/memorySlice'
import {
  clearCodegenId,
  connected,
  connectionError,
  copilotAbortCodegen,
  copilotConnect,
  copilotDisconnect,
  copilotInitializeAgent,
  copilotReconnect,
  copilotSendMessage,
  copilotSubmitMessage,
  resetReconnectAttempts,
  setCodegenId,
  setCodegenStatus,
  startReconnecting,
} from './copilotSlice'

// Constants
const MAX_RECONNECT_ATTEMPTS = 3
const LOADING_MESSAGE_TIMEOUT = 30000
const INITIALIZATION_TIMEOUT = 20000
const PROGRESS_BUFFER_DELAY = 500
const CONNECTION_DELAY = 500

// Helper functions
const isProgressEvent = (type: string) =>
  [
    'code_generation_started',
    'step_start',
    'step_stop',
    'step_failure',
    'steps_loaded',
    'app_metadata_generated',
    'code_generation_finished',
    'deploy_ended',
  ].includes(type)

const hasUnansweredClarifyingQuestionInMessages = (messages: ChatItem[]): boolean => {
  let lastSystemIdx = -1
  let lastSystemType = null

  messages.forEach((msg: ChatItem, idx: number) => {
    if (msg.sender === 'system') {
      lastSystemIdx = idx
      lastSystemType = msg.type
    }
  })

  if (lastSystemType !== 'clarifying') return false

  for (let i = lastSystemIdx + 1; i < messages.length; i++) {
    if (messages[i].sender === 'user') return false
  }

  return true
}

const createWebsocket = (profiling: string | null) => {
  const wsURL =
    process.env.NEXT_PUBLIC_BACKEND_WEBSOCKET_ENDPOINT_URI +
    '/v1/copilot' +
    (profiling ? '?profiling=true' : '')

  console.log('createWebsocket: connecting to', wsURL)
  return new WebSocket(wsURL)
}

// Message creation helpers
const createAssistantMessage = (message: string): MessageItem => ({
  messageId: uuidv4(),
  sender: 'assistant',
  message,
  typewriterEffect: true,
})

const createUserMessage = (message: string): MessageItem => ({
  messageId: uuidv4(),
  sender: 'user',
  message,
  typewriterEffect: true,
})

const createProgressMessage = (eventType: string): SystemMessageItem => ({
  messageId: uuidv4(),
  sender: 'system',
  type: 'progress',
  steps: [
    {
      id: eventType,
      label: eventType,
      status: 'in-progress',
      subtitles: shuffle(
        eventNameToUserFacingMessageMap[eventType as keyof typeof eventNameToUserFacingMessageMap]
      ),
    },
  ],
  expanded: false,
})

const createCheckpointMessage = (agent: any): SystemMessageItem => ({
  messageId: uuidv4(),
  sender: 'system',
  type: 'checkpoint',
  version: 1,
  name: agent?.name || '',
  description: agent?.description || '',
  updatedAt: agent?.updatedAt || new Date().toISOString(),
})

// Copilot middleware
// This middleware manages a single WebSocket connection per tab and handles:
// - Connection establishment and cleanup
// - Automatic reconnection with exponential backoff
// - Message routing and state management
// - Prevention of recursive action dispatching
export const copilotMiddleware: Middleware = (store) => {
  let socket: WebSocket | null = null
  let isReadyForMessages = false
  let wasCodegenAbortedRef = false
  let dateSubmittedMessage: Date | null = null
  let initializedCallback: (() => void) | null = null

  // Progress event buffering
  let clarificationInProgress = false
  let eventBuffer: any[] = []
  let progressBufferTimeout: NodeJS.Timeout | null = null
  let bufferedProgressEvents: any[] = []

  // Reconnection guard
  let isReconnecting = false
  let intentionalDisconnectSocket: WebSocket | null = null // Track which socket the intentional disconnect applies to
  let localReconnectAttempts = 0

  const sendWebsocketMessage = (message: ApiCodeGenerationRequest) => {
    if (socket?.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not ready for sending messages')
      return
    }
    socket.send(JSON.stringify(message))
    console.log('WebSocket message sent:', message)
  }

  const handleProgressEvent = (data: ApiCodeGenerationEvent) => {
    const state = store.getState()
    const lastProgressMessageId = state.chat.lastProgressMessageId
    const agentId = state.copilot.agentId

    if (clarificationInProgress && isProgressEvent(data.type)) {
      eventBuffer.push(data)
      return
    }

    console.log('handleProgressEvent', data.type)
    switch (data.type) {
      case 'project_initialization_started':
        store.dispatch(setCodegenStatus('initializing'))
        break
      case 'project_initialization_finished':
        if (data.resetStatus) {
          store.dispatch(setCodegenStatus('none'))
        }
        break
      case 'redeploy_started':
        store.dispatch(setCodegenStatus('redeploying'))
        break
      case 'code_generation_started': {
        store.dispatch(setCodegenId(data.codegenId))
        wasCodegenAbortedRef = false
        store.dispatch(setCodegenStatus('generating'))
        store.dispatch(removeAllLoadingMessages())
        break
      }
      case 'step_start': {
        switch (data.event) {
          case 'code_generation_steps_yaml':
            store.dispatch(showMemoryPanel(false))
            const progressMessage = createProgressMessage(data.event)
            store.dispatch(appendMessage(progressMessage))
            store.dispatch(setLastProgressMessageId(progressMessage.messageId))
            store.dispatch(setAgentDeployed(false)) // So we don't get the "app is ready" message when we're still generating the app
            break
          case 'code_generation_rag':
            store.dispatch(removeAllLoadingMessages())
            break
          case 'deployment':
            store.dispatch(setAgentDeployed(false))
            store.dispatch(setCodegenStatus('deploying'))
            refreshQuota()(store.dispatch)
            break
        }

        if (lastProgressMessageId) {
          store.dispatch(
            appendSubProgressMessage({
              messageId: lastProgressMessageId,
              step: {
                id: data.event,
                label: data.event,
                status: 'in-progress',
                subtitles: shuffle(
                  eventNameToUserFacingMessageMap[
                    data.event as string as keyof typeof eventNameToUserFacingMessageMap
                  ]
                ),
              },
            })
          )
        }

        break
      }
      case 'step_stop': {
        if (lastProgressMessageId) {
          store.dispatch(
            updateSubProgressMessage({
              messageId: lastProgressMessageId,
              stepId: data.event,
              status: 'success',
            })
          )
        }
        break
      }
      case 'step_failure': {
        if (lastProgressMessageId) {
          store.dispatch(
            updateSubProgressMessage({
              messageId: lastProgressMessageId,
              stepId: data.event,
              status: 'error',
              errorMessage: data.error || `Error during ${data.event} step`,
            })
          )
        }
        store.dispatch(setCodegenStatus('none'))
        break
      }
      case 'steps_loaded': {
        if (!data.steps) {
          console.log('Invalid Agent Steps propagated')
          break
        }

        const isReconnecting = state.copilot.isReconnecting

        if (!isReconnecting) {
          store.dispatch(setShowAppPanel(true))
          store.dispatch(setAgentSteps(data.steps))

          if (agentId) {
            const layoutSetting = getLayoutSetting()
            if (!layoutSetting.customizationPanelDisplayedBefore) {
              store.dispatch(setShowCustomizationPanel(true))
              updateLayoutSetting({ customizationPanelDisplayedBefore: true })
            }
          }
        }
        break
      }
      case 'app_metadata_generated': {
        store.dispatch(updateAgent({ name: data.appName, description: data.appDescription }))
        break
      }
      case 'prompts_loaded': {
        store.dispatch(setPrompts(data.prompts))
        store.dispatch(setPromptLoading(false))
        break
      }
      case 'modify_prompt_success': {
        store.dispatch(setPromptLoading(false))
        store.dispatch(setPrompts(data.updatedPrompts))
        break
      }
      case 'modify_prompt_failure': {
        store.dispatch(setPromptLoading(false))
        toast.error('Failed to modify prompt')
        break
      }
      case 'code_generation_finished': {
        const state = store.getState()
        const isReconnecting = state.copilot.isReconnecting

        if (!isReconnecting) {
          store.dispatch(setLastProgressMessageExpanded(false))
          store.dispatch(setMinimizeCustomizationPanel(true))
        }
        break
      }
      case 'agent': {
        const state = store.getState()
        const isReconnecting = state.copilot.isReconnecting
        const currentAgent = state.agent.agent

        if (!isReconnecting || !currentAgent) {
          store.dispatch(setAgent(data.agent))
        }

        isReadyForMessages = true
        if (initializedCallback) {
          initializedCallback()
          initializedCallback = null
        }
        break
      }
      case 'deploy_ended': {
        const state = store.getState()
        const isReconnecting = state.copilot.isReconnecting
        console.log(isReconnecting)

        if (!isReconnecting) {
          console.log('DEPLOYED IS TRUE')
          store.dispatch(setAgentDeployed(true))
          store.dispatch(setCodegenStatus('none'))

          if (agentId) {
            fetchAgent(agentId).then((freshAgent) => {
              store.dispatch(appendMessage(createCheckpointMessage(freshAgent.agent)))
            })
          }

          store.dispatch(setMinimizeCustomizationPanel(true))
        }

        store.dispatch(clearCodegenId())
        break
      }
      case 'copilot_thread_retrieved':
        const currentState = store.getState()
        const messages = currentState.chat.messages
        const currentMessageHash = currentState.chat.messageHash

        const messageContent = data.messages.map((message) => message.content)

        // Instead of a proper hash, we can do a quick and dirty comparison of message counts.
        const receivedMessagesHash = messagesHash(data.messages)

        if (
          wasCodegenAbortedRef ||
          messages.length === 0 ||
          currentMessageHash !== receivedMessagesHash
        ) {
          console.log(`Replacing messages: ${messageContent.length} messages`)

          wasCodegenAbortedRef = false

          // Convert all messages at once and append them in a single action
          const messageItems = data.messages.map((message) =>
            message.role === 'user'
              ? { ...createUserMessage(message.content), typewriterEffect: false }
              : { ...createAssistantMessage(message.content), typewriterEffect: false }
          )
          store.dispatch(
            replaceMessages({ messages: messageItems, messageHash: receivedMessagesHash })
          )
        }
        break
    }
  }

  const setupWebsocketEvents = () => {
    if (!socket) return

    const currentSocket = socket // Capture the socket reference for event handlers

    currentSocket.onopen = async () => {
      const state = store.getState()
      const wasReconnecting = isReconnecting

      console.log(
        `WebSocket connected! Was reconnecting: ${wasReconnecting}, Attempts before reset: ${localReconnectAttempts}`
      )

      isReconnecting = false // Reset the local reconnection flag

      // Connection delay to ensure stability
      await new Promise((resolve) => setTimeout(resolve, CONNECTION_DELAY))

      if (wasReconnecting) {
        console.log(
          `Successful reconnection after ${localReconnectAttempts} attempts, resetting counter`
        )

        if (localReconnectAttempts >= 2) {
          // Show success toast when reconnection succeeds
          toast.success('Connection restored!', {
            toastId: 'reconnection-success',
            autoClose: 1000,
          })
        }

        localReconnectAttempts = 0 // Reset reconnect attempts on successful connection
        // Sync Redux state with local state
        store.dispatch(resetReconnectAttempts())

        store.dispatch(setShowErrorWhileGeneration(false))
        store.dispatch(setErrorMessage(null))

        const codegenId = state.copilot.codegenId
        if (codegenId) {
          console.warn('Reconnected during active generation. Resetting state.')
          store.dispatch(removeAllLoadingMessages())
          store.dispatch(setShowErrorWhileGeneration(true))
          store.dispatch(
            setErrorMessage('The connection was reset during an operation. Please try again.')
          )
          store.dispatch(clearCodegenId())
        }
      }

      // Send initialize message
      const agentId = state.copilot.agentId
      if (agentId) {
        sendWebsocketMessage({
          type: 'initialize',
          agentId,
        })
      }
    }

    currentSocket.onmessage = (messageEvent: MessageEvent) => {
      const { event: data } = JSON.parse(messageEvent.data) as { event: ApiCodeGenerationEvent }
      console.log('WebSocket message received:', data)

      const state = store.getState()
      const agentId = state.copilot.agentId

      // Handle clarifying event immediately
      if (data.type === 'clarifying') {
        store.dispatch(
          appendMessage({
            messageId: `clarifying-${Date.now()}`,
            sender: 'system',
            type: 'clarifying',
            message: data.question || 'Please clarify your request.',
          })
        )
        store.dispatch(setClarificationInProgress(true))
        store.dispatch(setCodegenStatus('none'))
        clarificationInProgress = true

        // Cancel buffered progress events
        if (progressBufferTimeout) {
          clearTimeout(progressBufferTimeout)
          progressBufferTimeout = null
          bufferedProgressEvents = []
        }
        return
      }

      // Handle progress events with buffering
      if (isProgressEvent(data.type)) {
        bufferedProgressEvents.push(data)
        if (!progressBufferTimeout) {
          progressBufferTimeout = setTimeout(function processBufferedProgress() {
            const currentState = store.getState()
            const messages = currentState.chat.messages

            if (!clarificationInProgress && !hasUnansweredClarifyingQuestionInMessages(messages)) {
              bufferedProgressEvents.forEach((ev) => handleProgressEvent(ev))
              bufferedProgressEvents = []
              progressBufferTimeout = null
            } else {
              progressBufferTimeout = setTimeout(processBufferedProgress, 1000)
            }
          }, PROGRESS_BUFFER_DELAY)
        }
        return
      }

      // Handle other events
      switch (data.type) {
        case 'connected':
          store.dispatch(connected({ connectionId: data.connectionId }))
          if (agentId) {
            analytics.copilot.copilotConnected(agentId, data.connectionId)
          }
          break

        case 'message':
          store.dispatch(removeAllLoadingMessages())
          store.dispatch(appendMessage(createAssistantMessage(data.message)))
          break

        case 'failure':
          store.dispatch(removeAllLoadingMessages())
          store.dispatch(setShowErrorWhileGeneration(true))
          store.dispatch(
            setErrorMessage(data.error || 'An error occurred while generating your app')
          )
          store.dispatch(setCodegenStatus('none'))

          toast.error(`An error occurred while generating your app: ${data.error}`, {
            toastId: `app-generation-error-${data.error}`,
          })
          break
        case 'user_not_authorized':
          // Intentional disconnect
          intentionalDisconnectSocket = currentSocket
          currentSocket.close()

          toast.error('Authorization Error, please try signing in again')

          // Sign out the user
          signOut(false)(store.dispatch)
          break
        default:
          // Handle other event types through the progress handler
          handleProgressEvent(data)
          break
      }

      // Track analytics
      if (agentId) {
        const properties: Record<string, string | undefined> = {
          agent_id: agentId,
          codegen_id: state.copilot.codegenId,
          connection_id: state.copilot.connectionId,
        }

        if (dateSubmittedMessage) {
          const codegenTimeMs = new Date().getTime() - dateSubmittedMessage.getTime()
          properties.codegen_time_ms = codegenTimeMs.toString()
        }

        analytics.copilot.copilotOnMessageReceived({
          ...properties,
          type: data.type,
        })
      }
    }

    currentSocket.onclose = () => {
      console.log('WebSocket disconnected')

      // Don't try to reconnect if this was an intentional disconnect for this specific socket
      const isIntentionalDisconnect = intentionalDisconnectSocket === currentSocket
      if (isIntentionalDisconnect) {
        console.log('Intentional disconnect, not attempting to reconnect')
        // Perform cleanup for intentional disconnects
        isReadyForMessages = false
        wasCodegenAbortedRef = false
        dateSubmittedMessage = null
        initializedCallback = null
        clarificationInProgress = false
        eventBuffer = []
        if (progressBufferTimeout) {
          clearTimeout(progressBufferTimeout)
          progressBufferTimeout = null
        }
        bufferedProgressEvents = []
        isReconnecting = false
        intentionalDisconnectSocket = null
        localReconnectAttempts = 0

        // Reset Redux reconnection state
        store.dispatch(resetReconnectAttempts())

        // Clear any error state
        store.dispatch(setShowErrorWhileGeneration(false))
        store.dispatch(setErrorMessage(null))
        return
      }

      // Prevent multiple simultaneous reconnection attempts
      if (isReconnecting) {
        console.log('Reconnection already in progress, skipping')
        return
      }

      console.log(
        `Current reconnection attempts: ${localReconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`
      )

      // Use a longer delay and don't dispatch actions immediately
      setTimeout(() => {
        console.log(
          `Timeout fired: attempts=${localReconnectAttempts}, isReconnecting=${isReconnecting}, intentionalDisconnect=${currentSocket === intentionalDisconnectSocket}`
        )

        if (
          localReconnectAttempts < MAX_RECONNECT_ATTEMPTS &&
          !isReconnecting &&
          currentSocket !== intentionalDisconnectSocket
        ) {
          localReconnectAttempts += 1
          console.log(
            `Starting reconnection attempt ${localReconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`
          )

          // Show info toast after first retry failure
          if (localReconnectAttempts === 2) {
            toast.info('Connection lost. Trying to reconnect...', {
              toastId: 'reconnection-attempt',
              autoClose: 3000,
            })
          }

          isReconnecting = true

          // Sync Redux state with local state
          store.dispatch(startReconnecting())

          // Get fresh state and reconnect
          const currentState = store.getState().copilot
          if (currentState.agentId) {
            console.log(`Calling connectWebsocket with agentId: ${currentState.agentId}`)
            connectWebsocket(currentState.agentId, currentState.profiling || null)
          } else {
            console.log('No agentId found, cannot reconnect')
            isReconnecting = false
          }
        } else if (localReconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Max reconnection attempts reached, giving up')

          const errorMessage =
            'Connection lost after multiple reconnection attempts. Please refresh the page to continue.'

          // Update Redux state to show connection error
          store.dispatch(connectionError(errorMessage))

          // Also use the existing layout error system for UI display
          store.dispatch(setShowErrorWhileGeneration(true))
          store.dispatch(setErrorMessage(errorMessage))

          toast.error(errorMessage)
        } else {
          console.log(
            `Skipping reconnection: attempts=${localReconnectAttempts}, isReconnecting=${isReconnecting}, intentionalDisconnect=${currentSocket === intentionalDisconnectSocket}`
          )
        }
      }, 2000) // Longer delay to prevent rapid reconnection attempts
    }

    currentSocket.onerror = (event) => {
      console.error('WebSocket error:', event)
      isReconnecting = false // Reset reconnection flag on error
      // Don't increment attempts here - let onclose handle the reconnection logic
    }
  }

  const connectWebsocket = (agentId: string, profiling?: string | null) => {
    console.log(
      `connectWebsocket called: agentId=${agentId}, attempts=${localReconnectAttempts}, isReconnecting=${isReconnecting}`
    )

    // Prevent multiple simultaneous connection attempts
    if (socket && socket.readyState === WebSocket.CONNECTING) {
      console.log('Connection already in progress, skipping')
      return
    }

    // Close existing connection if any
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.log(`Closing existing socket (readyState: ${socket.readyState})`)
      // Mark as intentional disconnect to prevent reconnection attempts from the old socket
      intentionalDisconnectSocket = socket
      socket.close()
      socket = null
      // Note: Don't reset intentionalDisconnect here - let the onclose handler reset it
      // after it has processed the intentional disconnect
    }

    try {
      console.log('Creating new WebSocket...')
      socket = createWebsocket(profiling || null)
      setupWebsocketEvents()
      console.log(`WebSocket created with readyState: ${socket.readyState}`)
    } catch (error) {
      console.error('Failed to create WebSocket:', error)
      isReconnecting = false // Reset flag if connection creation fails
    }
  }

  return (next) => (action: any) => {
    const result = next(action)
    const state = store.getState()

    // Handle websocket actions
    if (copilotConnect.match(action)) {
      connectWebsocket(action.payload.agentId, action.payload.profiling)
    }

    if (copilotDisconnect.match(action)) {
      if (socket) {
        intentionalDisconnectSocket = socket
        socket.close()
        socket = null
      }
    }

    if (copilotReconnect.match(action)) {
      // Reconnection is now handled directly in onclose to avoid recursion
      // This action is kept for external triggers if needed
      const { agentId, profiling } = state.copilot
      if (agentId) {
        connectWebsocket(agentId, profiling || null)
      }
    }

    if (copilotSendMessage.match(action)) {
      sendWebsocketMessage(action.payload)
    }

    if (copilotSubmitMessage.match(action)) {
      const { message, agentId, appendLocally = true } = action.payload

      if (appendLocally) {
        store.dispatch(appendMessage(createUserMessage(message)))
        const loadingMessageId = uuidv4()
        store.dispatch(addLoadingMessage(loadingMessageId))

        setTimeout(() => {
          store.dispatch(removeAllLoadingMessages())
        }, LOADING_MESSAGE_TIMEOUT)
      }

      if (!isReadyForMessages) {
        const timeout = setTimeout(() => {
          store.dispatch(removeAllLoadingMessages())
          store.dispatch(
            appendMessage(
              createAssistantMessage('Failed to connect to the server. Please try again later.')
            )
          )
        }, INITIALIZATION_TIMEOUT)

        initializedCallback = () => {
          clearTimeout(timeout)
          store.dispatch(copilotSubmitMessage({ message, agentId, appendLocally: false }))
        }
        return result
      }

      sendWebsocketMessage({
        type: 'message',
        message,
      })

      dateSubmittedMessage = new Date()

      // Analytics
      analytics.copilot.copilotMessageSent({
        agent_id: agentId,
        codegen_id: state.copilot.codegenId,
        connection_id: state.copilot.connectionId,
        message_content: message,
        message_length: message.length.toString(),
        success: 'true',
      })
    }

    if (copilotAbortCodegen.match(action)) {
      wasCodegenAbortedRef = true
      sendWebsocketMessage({
        type: 'cancel',
      })
    }

    if (copilotInitializeAgent.match(action)) {
      sendWebsocketMessage({
        type: 'initialize',
        agentId: action.payload.agentId,
      })
    }

    // Handle clarification state changes
    if (action.type === 'chat/setClarificationInProgress') {
      clarificationInProgress = action.payload
      if (!clarificationInProgress && eventBuffer.length > 0) {
        setTimeout(() => {
          eventBuffer.forEach((ev) => handleProgressEvent(ev))
          eventBuffer = []
        }, 800)
      }
    }

    return result
  }
}
